import { useMutation, useQuery } from "@apollo/client/react";
import { Dialog, Transition } from "@headlessui/react";
import {
  NewspaperIcon,
  PencilIcon,
  ReceiptRefundIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon, InboxArrowDownIcon } from "@heroicons/react/24/solid";
import { Fragment, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Waiting } from "../../../../../animations";
import { ButtonDropdown } from "../../../../../components/appearance";
import { ErrorFallback, Head } from "../../../../../components/core";
import { Button } from "../../../../../components/form";
import { VehicleBasic } from "../../../../../graphql/fleets/vehicles";
import {
  GET_VEHICLEISSUE_BY_ID,
  UPDATE_VEHICLEISSUE_STATUS,
  VehicleIssue,
  VehicleIssueStatus,
  VehicleIssueStatusForm,
} from "../../../../../graphql/fleets/vehicles/pages/issues";
import { AuthShield } from "../../../../auth/core";
import { IssuesDetail } from "../../components/IssuesDetail";
import IssueStatusForm from "../../components/IssuesStatusForm";

const IssuesView = ({
  breadcrumbs,
  vehicleBasic,
}: {
  breadcrumbs: Breadcrumb[];
  vehicleBasic: VehicleBasic | null;
}) => {
  let { vehicleId, issueId } = useParams();
  const { t } = useTranslation();
  const [openStatus, setOpenStatus] = useState(false);

  const { data, loading, error, refetch } = useQuery<{
    fetchVehicleIssue: VehicleIssue;
  }>(GET_VEHICLEISSUE_BY_ID, {
    variables: {
      id: issueId,
    },
  });

  const issue = useMemo<VehicleIssue | undefined>(
    () => data?.fetchVehicleIssue,
    [data]
  );

  const [updateVehicleIssueStatus] = useMutation(UPDATE_VEHICLEISSUE_STATUS, {
    refetchQueries: [
      { query: GET_VEHICLEISSUE_BY_ID, variables: { id: issueId } },
    ],
  });
  const handleSubmitStatus = useCallback(
    (
      values: VehicleIssueStatusForm,
      actions: { setSubmitting: (arg0: boolean) => void }
    ) => {
      if (!issueId || !issue) return;
      updateVehicleIssueStatus({
        variables: {
          id: issueId,
          status:
            issue.status === VehicleIssueStatus.OPEN
              ? VehicleIssueStatus.RESOLVED
              : VehicleIssueStatus.OPEN,
          statusNote: values.statusNotes,
        },
      })
        .then(async ({ data }) => {
          if (data?.vehicleIssueStatusUpdate) {
            await refetch();
            actions.setSubmitting(false);
            handleCancelStatus();
            toast.success(`Vehicle issue updated successfully`);
          } else {
            actions.setSubmitting(false);
            toast.error("Something went wrong, please try again later");
          }
        })
        .catch((error) => {
          actions.setSubmitting(false);
          toast.error(error.message);
        });
    },
    [issue, issueId, refetch, updateVehicleIssueStatus]
  );

  const handleCancelStatus = () => {
    setOpenStatus(false);
  };

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={IssuesViewResource.name}
        heading={IssuesViewResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: IssuesViewResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_issue")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_issue")}
              </p>
            </div>
            {issue && issue?.status !== VehicleIssueStatus.RESOLVED ? (
              <div className="ml-auto flex items-center space-x-2 pr-3">
                <AuthShield access={["read-vehicles"]}>
                  <Button
                    type="link"
                    href={`/fleets/vehicles/issues/${issueId}/edit`}
                  >
                    <PencilIcon className="mr-2 h-5 w-5" />
                    {t("text_edit")}
                  </Button>
                </AuthShield>

                <ButtonDropdown
                  variant="tertiary"
                  childrens={[
                    {
                      label: "Add to Service Entry",
                      href: `/fleets/vehicles/service-entries/add?vehicleId=${vehicleId}&issueId=${issueId}`,
                      target: "_blank",
                      icon: () => (
                        <WrenchIcon
                          className="mr-2 h-5 w-5 text-current"
                          aria-hidden="true"
                        />
                      ),
                    },
                    {
                      label: "Resolve with Note",
                      onClick: () => {
                        setOpenStatus(true);
                      },
                      icon: () => (
                        <NewspaperIcon
                          className="mr-2 h-5 w-5 text-current"
                          aria-hidden="true"
                        />
                      ),
                    },
                  ]}
                >
                  <InboxArrowDownIcon className="mr-2 h-5 w-5" />
                  {t("text_resolve")}
                  <ChevronDownIcon
                    className="ml-1 h-5 w-5"
                    aria-hidden="true"
                  />
                </ButtonDropdown>
              </div>
            ) : (
              <AuthShield access={["update-vehicles"]}>
                <Button
                  onClick={() => {
                    setOpenStatus(true);
                  }}
                >
                  <ReceiptRefundIcon className="mr-2 h-5 w-5" />
                  {t("text_reopen_issue")}
                </Button>
              </AuthShield>
            )}
          </div>

          {loading || !issue ? (
            <Waiting />
          ) : (
            <IssuesDetail issue={issue} vehicleBasic={vehicleBasic} />
          )}
        </div>
      </div>
      <Transition.Root show={openStatus} as={Fragment} appear>
        <Dialog as="div" className="relative z-10" onClose={handleCancelStatus}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-gray-100 rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                {issue ? (
                  <IssueStatusForm
                    heading={
                      issue.status === VehicleIssueStatus.OPEN
                        ? "Resolve Issue"
                        : "Reopen Issue"
                    }
                    caption={
                      issue.status === VehicleIssueStatus.OPEN
                        ? "Resolve this issue by adding a note."
                        : "Reopen this issue by adding a note."
                    }
                    button={
                      issue.status === VehicleIssueStatus.OPEN
                        ? "Resolve"
                        : "Reopen"
                    }
                    initialValues={{
                      vehicleId: issue.vehicleId,
                      statusNotes: issue?.statusNotes || "",
                    }}
                    onSubmit={handleSubmitStatus}
                    onCancel={handleCancelStatus}
                  />
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default IssuesView;
export const IssuesViewResource: ResourceProps = {
  name: "View Issue",
  description: "View Issue of a Vehicle",
  access: ["read-vehicles"],
  path: "/issue/:issueId",
};
